import React, {
  SetStateAction,
  useEffect,
  Dispatch,
  useMemo,
  useCallback,
} from "react";
import { useIntl } from "react-intl";

import { getMembersList } from "src/redux";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/reducers";
import CustomSelect from "src/app/components/CustomSelect/CustomSelect";
import AvatarPlaceholder from "src/images/avatar-purple.svg";
import { translateMessage } from "src/app/methods/translateMessage";
import { ReactSelectOptionWithReactNodeLabel } from "src/types";

interface ActivityFiltersMemberSelectProps {
  selectedMemberUuids: string[];
  setSelectedMemberUuids: Dispatch<SetStateAction<string[]>>;
}

export default function ActivityFiltersMemberSelect({
  selectedMemberUuids,
  setSelectedMemberUuids,
}: ActivityFiltersMemberSelectProps) {
  const dispatch = useDispatch();

  const membersList = useSelector(
    (state: RootState) => state.projectReducer.activeMembersList,
  );

  const activeWorkspaceUuid = useSelector(
    (state: RootState) => state.mainReducer.activeWorkspaceUuid,
  );

  const intl = useIntl();

  const getMembers = useCallback(() => {
    dispatch(getMembersList(activeWorkspaceUuid));
  }, [dispatch, activeWorkspaceUuid]);

  const visibleMembers = useMemo(
    () =>
      membersList.filter(
        (member) => !member?.hideOnMembersList && member?.status !== "removed",
      ),
    [membersList],
  );

  const memberOptions = useMemo(
    () =>
      visibleMembers.map((member) => ({
        value: member.id,
        label: (
          <span className="activity-filters__option">
            <img
              loading="lazy"
              className="activity-filters__option-image"
              src={member?.avatarUrl || AvatarPlaceholder}
              alt="avatar"
            />
            <span className="activity-filters__option-text">{member.name}</span>
          </span>
        ),
      })),
    [visibleMembers],
  );

  const filterOption = (candidate: any, input: string) => {
    const foundMember = visibleMembers.find(
      (member) => member.id === candidate.value,
    );
    return (
      candidate.data.__isNew__ ||
      foundMember?.name.toUpperCase().includes(input.toUpperCase())
    );
  };

  useEffect(() => {
    getMembers();
  }, [getMembers]);

  return (
    <CustomSelect
      value={memberOptions.filter((member) =>
        selectedMemberUuids.includes(member.value),
      )}
      onChange={(newOption: ReactSelectOptionWithReactNodeLabel[]) => {
        setSelectedMemberUuids(newOption.map((option) => option.value));
      }}
      placeholder={translateMessage({
        intl,
        id: "ws_all",
        defaultMessage: "All",
      })}
      options={memberOptions}
      disabled={memberOptions.length === 0}
      isMulti
      withTiles
      openMenuOnFocus={false}
      isSearchable
      filterOption={filterOption}
    />
  );
}
